import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { StockRevaluation } from './stock-revaluation.model';

@Injectable({
  providedIn: 'root'
})
export class StockRevaluationService {

  private _endpoint: string = environment.api.endpoint;

  constructor(private httpClient: HttpClient) { }

  /**
   * Posts a revaluation. Its own route on the backend so it is nameable as its own permission -
   * moving money is not the same right as moving stock.
   *
   * The whole batch goes up in one request because the backend runs it as one transaction. Posting
   * line by line would allow a partial post, which on a correction means a product left holding half
   * of one.
   *
   * The body carries no `qty` key at all, by design. The backend writes zero itself; sending one
   * would be sending a quantity into a path whose whole point is that it has none.
   *
   * Each line states `targetValue`: what the product's whole holding is worth after the correction.
   * It carries no `valueAdjustment`, and must not be made to again - the backend refuses any body
   * holding that key outright rather than ignoring it, because the two fields mean opposite things
   * and a dropped field would have answered 2xx to a write-down that never happened. That guard is
   * what surfaced this client having been left behind when the contract changed.
   *
   * @param revaluation the reference plus the lines to post
   *
   * @returns Observable<any> the movements the backend wrote
   */
  revalue(revaluation: StockRevaluation): Observable<any> {

    if (!revaluation) {
      throw new Error('stock revaluation service: you must supply a revaluation object for saving');
    }

    if (!revaluation.lines || revaluation.lines.length === 0) {
      throw new Error('stock revaluation service: a revaluation must have at least one line');
    }

    return this.httpClient.post(this._endpoint + 'product-stock-movement/revaluation',
      JSON.stringify(revaluation),
      { headers: { 'Content-Type': 'application/json' } }).pipe(map((data) => {
        return data;
      }));
  }
}
